/**
 * Expenses Tab - Expense rate management, import/export, copy country defaults
 */

import React, { useState, useRef, useMemo } from 'react';
import {
  X,
  Plus,
  Globe,
  Trash2,
  DollarSign,
  Copy,
  Upload,
  ChevronDown,
  ChevronUp,
  Download,
} from 'lucide-react';
import type { CustomMarketplace, CategoryColumnDefaults, PricingCalculatorImport } from '../types';
import type { Product } from '../../../types';
import {
  importFromProfitAnalyzer,
  previewProfitAnalyzerImport,
  importBulkFromProfitAnalyzer,
  previewBulkImport,
  copyCountryDefaults,
  getCountriesWithDefaults,
  isV2Import,
  isV1Category,
  type BulkPricingImport,
} from '../../../services/profitAnalyzerImport';
import {
  getCategoryDefaultsByMarketplace,
  upsertCategoryColumnDefaults,
  deleteCategoryColumnDefaults,
  bulkUpsertCategoryColumnDefaults,
} from '../../../utils/database';
import { logger } from '../../../utils/logger';
import { useModalA11y } from '../../../hooks/useModalA11y';

interface ExpensesTabProps {
  selectedMarketplace: CustomMarketplace;
  products: Product[];
  categoryDefaults: CategoryColumnDefaults[];
  setCategoryDefaults: (defaults: CategoryColumnDefaults[] | ((prev: CategoryColumnDefaults[]) => CategoryColumnDefaults[])) => void;
}

const ExpensesTab: React.FC<ExpensesTabProps> = ({
  selectedMarketplace,
  products,
  categoryDefaults,
  setCategoryDefaults,
}) => {
  // Expense filter state
  const [expenseFilterCountry, setExpenseFilterCountry] = useState<string>('');
  const [expenseFilterCategory, setExpenseFilterCategory] = useState<string>('');
  const [expenseFilterFulfillment, setExpenseFilterFulfillment] = useState<string>('');
  const [expandedExpenseId, setExpandedExpenseId] = useState<string | null>(null);
  const [isAddingExpense, setIsAddingExpense] = useState(false);
  const [newExpense, setNewExpense] = useState<{
    countryCode: string;
    category: string;
    fulfillmentType: string;
    targetMargin: number;
    columnDefaults: Record<string, number | string>;
  }>({
    countryCode: '',
    category: '',
    fulfillmentType: '',
    targetMargin: 20,
    columnDefaults: {},
  });
  const [copyFromExpense, setCopyFromExpense] = useState<CategoryColumnDefaults | null>(null);

  // Profit Analyzer import state
  const [profitImportPreview, setProfitImportPreview] = useState<PricingCalculatorImport | null>(null);
  const [profitImportFile, setProfitImportFile] = useState<File | null>(null);
  const [isProfitImporting, setIsProfitImporting] = useState(false);
  const profitImportRef = useRef<HTMLInputElement>(null);

  // Bulk import state
  const [bulkImportPreview, setBulkImportPreview] = useState<BulkPricingImport | null>(null);
  const [bulkImportFile, setBulkImportFile] = useState<File | null>(null);
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  const bulkImportRef = useRef<HTMLInputElement>(null);

  // Copy country defaults state
  const [showCopyCountryModal, setShowCopyCountryModal] = useState(false);
  const [countriesWithData, setCountriesWithData] = useState<{ countryCode: string; categoryCount: number; fulfillmentTypes: string[] }[]>([]);
  const [copyCountrySource, setCopyCountrySource] = useState('');
  const [copyCountryTarget, setCopyCountryTarget] = useState('');
  const [copyFulfillmentType, setCopyFulfillmentType] = useState<'FBA' | 'FBM' | 'both'>('both');
  const [copyOverwrite, setCopyOverwrite] = useState(false);
  const [isCopyingCountry, setIsCopyingCountry] = useState(false);

  // Modal a11y refs (Escape + initial focus)
  const copyCountryModalRef = useModalA11y(showCopyCountryModal, () => setShowCopyCountryModal(false));
  const addExpenseModalRef = useModalA11y(isAddingExpense, () => { setIsAddingExpense(false); setCopyFromExpense(null); });

  // Get unique categories from products
  const categories = useMemo(() => {
    const cats = new Set<string>();
    products.forEach(p => {
      if (p.category) cats.add(p.category);
    });
    return Array.from(cats).sort();
  }, [products]);

  // Profit Analyzer import handlers
  const handleProfitImportSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const preview = await previewProfitAnalyzerImport(file);
      setProfitImportPreview(preview);
      setProfitImportFile(file);
    } catch (error) {
      logger.error('Preview failed:', error);
      alert('Dosya okunamad\u0131. Ge\u00E7erli bir Profit Analyzer export dosyas\u0131 se\u00E7in.');
    }

    if (profitImportRef.current) {
      profitImportRef.current.value = '';
    }
  };

  const handleProfitImportConfirm = async () => {
    if (!profitImportFile) return;

    setIsProfitImporting(true);
    try {
      const result = await importFromProfitAnalyzer(profitImportFile);
      alert(
        `Import ba\u015Far\u0131l\u0131!\n` +
        `${result.marketplace} pazar\u0131 i\u00E7in ${result.categories.length} kategori i\u015Flendi.\n` +
        `Yeni: ${result.imported}, G\u00FCncellenen: ${result.updated}, Atlanan: ${result.skipped}`
      );
      setProfitImportPreview(null);
      setProfitImportFile(null);
      const defaults = await getCategoryDefaultsByMarketplace(selectedMarketplace.id);
      setCategoryDefaults(defaults);
    } catch (error) {
      logger.error('Import failed:', error);
      alert('Import hatas\u0131! L\u00FCtfen dosyay\u0131 kontrol edin.');
    } finally {
      setIsProfitImporting(false);
    }
  };

  const handleProfitImportCancel = () => {
    setProfitImportPreview(null);
    setProfitImportFile(null);
  };

  // Bulk import handlers
  const handleBulkImportSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const preview = await previewBulkImport(file);
      setBulkImportPreview(preview);
      setBulkImportFile(file);
    } catch (error) {
      logger.error('Bulk preview failed:', error);
      alert('Dosya okunamad\u0131. Ge\u00E7erli bir Amazon Analyzer bulk export dosyas\u0131 se\u00E7in.');
    }

    if (bulkImportRef.current) {
      bulkImportRef.current.value = '';
    }
  };

  const handleBulkImportConfirm = async () => {
    if (!bulkImportFile) return;

    setIsBulkImporting(true);
    try {
      const result = await importBulkFromProfitAnalyzer(bulkImportFile);
      const marketplaceList = Object.keys(result.byMarketplace).join(', ');
      alert(
        `Bulk Import ba\u015Far\u0131l\u0131!\n\n` +
        `Toplam: ${result.totalImported} yeni, ${result.totalUpdated} g\u00FCncellenen, ${result.totalSkipped} atlanan\n` +
        `Pazarlar: ${marketplaceList}`
      );
      setBulkImportPreview(null);
      setBulkImportFile(null);
      const defaults = await getCategoryDefaultsByMarketplace(selectedMarketplace.id);
      setCategoryDefaults(defaults);
    } catch (error) {
      logger.error('Bulk import failed:', error);
      alert('Import hatas\u0131! L\u00FCtfen dosyay\u0131 kontrol edin.');
    } finally {
      setIsBulkImporting(false);
    }
  };

  const handleBulkImportCancel = () => {
    setBulkImportPreview(null);
    setBulkImportFile(null);
  };

  // Copy country handlers
  const handleOpenCopyCountryModal = async () => {
    try {
      const countries = await getCountriesWithDefaults();
      setCountriesWithData(countries);
      setCopyCountrySource(countries[0]?.countryCode || '');
      setCopyCountryTarget('');
      setCopyFulfillmentType('both');
      setCopyOverwrite(false);
      setShowCopyCountryModal(true);
    } catch (error) {
      logger.error('Failed to get countries with data:', error);
      alert('\u00DClke listesi al\u0131namad\u0131.');
    }
  };

  const handleCopyCountryConfirm = async () => {
    if (!copyCountrySource || !copyCountryTarget) {
      alert('Kaynak ve hedef \u00FClke se\u00E7melisiniz.');
      return;
    }

    if (copyCountrySource === copyCountryTarget) {
      alert('Kaynak ve hedef \u00FClke ayn\u0131 olamaz.');
      return;
    }

    setIsCopyingCountry(true);
    try {
      const result = await copyCountryDefaults(copyCountrySource, copyCountryTarget, {
        fulfillmentType: copyFulfillmentType,
        overwriteExisting: copyOverwrite,
      });

      alert(
        `\u00DClke verisi kopyaland\u0131!\n\n` +
        `${copyCountrySource} \u2192 ${copyCountryTarget}\n` +
        `Kopyalanan: ${result.copied}, Atlanan: ${result.skipped}\n` +
        `Kategoriler: ${result.categories.join(', ')}`
      );

      setShowCopyCountryModal(false);
      const defaults = await getCategoryDefaultsByMarketplace(selectedMarketplace.id);
      setCategoryDefaults(defaults);
    } catch (error) {
      logger.error('Copy failed:', error);
      alert('Kopyalama hatas\u0131: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
    } finally {
      setIsCopyingCountry(false);
    }
  };

  // CSV Export for expense rates
  const handleExportExpenseRates = () => {
    const data = categoryDefaults.map(d => ({
      country: d.countryCode,
      category: d.category,
      fulfillment: d.fulfillmentType,
      targetMargin: d.targetMargin,
      ...d.columnDefaults,
    }));

    const headers = ['country', 'category', 'fulfillment', 'targetMargin', ...selectedMarketplace.columns.map(c => c.key)];
    const csv = [
      headers.join(','),
      ...data.map(row => headers.map(h => row[h as keyof typeof row] ?? '').join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedMarketplace.shortName}_expense_rates.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // CSV Import for expense rates
  const handleImportExpenseRates = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) return;

    const headers = lines[0].split(',');
    const rows = lines.slice(1).map(line => {
      const values = line.split(',');
      const row: Record<string, string | number> = {};
      headers.forEach((h, i) => {
        row[h] = values[i];
      });
      return row;
    });

    const imports: Partial<CategoryColumnDefaults>[] = rows.map(row => ({
      marketplaceId: selectedMarketplace.id,
      countryCode: String(row.country || ''),
      category: String(row.category || ''),
      fulfillmentType: String(row.fulfillment || ''),
      targetMargin: Number(row.targetMargin) || 20,
      columnDefaults: Object.fromEntries(
        selectedMarketplace.columns.map(c => [c.key, Number(row[c.key]) || 0])
      ),
      dataSource: 'manual' as const,
    }));

    await bulkUpsertCategoryColumnDefaults(imports as CategoryColumnDefaults[]);
    const defaults = await getCategoryDefaultsByMarketplace(selectedMarketplace.id);
    setCategoryDefaults(defaults);
    alert(`${imports.length} gider oran\u0131 aktar\u0131ld\u0131.`);
  };

  return (
    <div className="space-y-4">
      {/* Import Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Single Marketplace Import */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-purple-600" />
              <h4 className="font-medium text-purple-900">Tek &#xDC;lke Import</h4>
            </div>
            <input
              ref={profitImportRef}
              type="file"
              accept=".json"
              onChange={handleProfitImportSelect}
              className="hidden"
            />
            <button
              onClick={() => profitImportRef.current?.click()}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-purple-700 bg-white hover:bg-purple-50 border border-purple-200 rounded-lg"
            >
              <Upload className="w-4 h-4" />
              JSON Se&#xE7;
            </button>
          </div>
          <p className="text-xs text-purple-700">
            Tek &#xFC;lke i&#xE7;in export edilmi&#x15F; JSON dosyas&#x131;n&#x131; y&#xFC;kleyin.
          </p>

          {/* Single Import Preview */}
          {profitImportPreview && (
            <div className="mt-4 bg-white rounded-lg border border-purple-200 p-4">
              <h5 className="font-medium text-slate-800 mb-2">Import &#xD6;nizleme</h5>
              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div>
                  <span className="text-slate-500">Pazaryeri:</span>
                  <span className="ml-2 font-medium">
                    {isV2Import(profitImportPreview)
                      ? Object.keys(profitImportPreview.refundRecoveryRates).join(', ')
                      : profitImportPreview.marketplace}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">D&#xF6;nem:</span>
                  <span className="ml-2 font-medium">{profitImportPreview.dateRange?.start} - {profitImportPreview.dateRange?.end}</span>
                </div>
                <div>
                  <span className="text-slate-500">Kategoriler:</span>
                  <span className="ml-2 font-medium">{profitImportPreview.categories.length}</span>
                </div>
              </div>
              <div className="max-h-40 overflow-y-auto mb-4">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">Kategori</th>
                      <th className="px-2 py-1 text-right">Hedef Marj</th>
                      <th className="px-2 py-1 text-right">Fee %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profitImportPreview.categories.map((cat, i) => {
                      const isV1 = isV1Category(cat);
                      const globalAdvertising = isV2Import(profitImportPreview)
                        ? profitImportPreview.globalSettings.advertisingPercent
                        : 0;
                      const advertisingPercent = isV1 ? cat.advertisingPercent : globalAdvertising;

                      return (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-2 py-1">{cat.category}</td>
                          <td className="px-2 py-1 text-right">
                            {isV1 ? cat.avgProfitMargin?.toFixed(1) : '15.0'}%
                          </td>
                          <td className="px-2 py-1 text-right">
                            {(cat.sellingFeePercent + cat.fbaFeePercent + advertisingPercent).toFixed(1)}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={handleProfitImportCancel}
                  className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded"
                >
                  &#x130;ptal
                </button>
                <button
                  onClick={handleProfitImportConfirm}
                  disabled={isProfitImporting}
                  className="px-3 py-1.5 text-sm text-white bg-purple-600 hover:bg-purple-700 rounded disabled:opacity-50"
                >
                  {isProfitImporting ? '\u0130\u015Fleniyor...' : 'Import Et'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Bulk Import (All Marketplaces) */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-green-600" />
              <h4 className="font-medium text-green-900">Bulk Import (T&#xFC;m &#xDC;lkeler)</h4>
            </div>
            <input
              ref={bulkImportRef}
              type="file"
              accept=".json"
              onChange={handleBulkImportSelect}
              className="hidden"
            />
            <button
              onClick={() => bulkImportRef.current?.click()}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-700 bg-white hover:bg-green-50 border border-green-200 rounded-lg"
            >
              <Upload className="w-4 h-4" />
              JSON Se&#xE7;
            </button>
          </div>
          <p className="text-xs text-green-700">
            Amazon Analyzer'dan "All Marketplaces" se&#xE7;ili iken export edilen JSON'u y&#xFC;kleyin.
            T&#xFC;m &#xFC;lkelerin FBA+FBM verileri tek seferde import edilir.
          </p>

          {/* Bulk Import Preview */}
          {bulkImportPreview && (
            <div className="mt-4 bg-white rounded-lg border border-green-200 p-4">
              <h5 className="font-medium text-slate-800 mb-2">Bulk Import &#xD6;nizleme</h5>
              <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                <div>
                  <span className="text-slate-500">&#xDC;lke:</span>
                  <span className="ml-2 font-medium">{bulkImportPreview.summary.totalMarketplaces}</span>
                </div>
                <div>
                  <span className="text-slate-500">Kategori:</span>
                  <span className="ml-2 font-medium">{bulkImportPreview.summary.totalCategories}</span>
                </div>
                {bulkImportPreview.summary.totalRevenue !== undefined && (
                  <div>
                    <span className="text-slate-500">Toplam Ciro:</span>
                    <span className="ml-2 font-medium">${bulkImportPreview.summary.totalRevenue.toLocaleString()}</span>
                  </div>
                )}
              </div>
              <div className="max-h-40 overflow-y-auto mb-4">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">&#xDC;lke</th>
                      <th className="px-2 py-1 text-right">Kategori</th>
                      <th className="px-2 py-1 text-right">Ciro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(bulkImportPreview.marketplaces).map(([code, data]) => {
                      const isV2 = isV2Import(data);
                      const revenue = isV2 ? 'N/A' : `$${data.summary.totalRevenue?.toLocaleString() || '0'}`;

                      return (
                        <tr key={code} className="border-t border-slate-100">
                          <td className="px-2 py-1 font-medium">{code}</td>
                          <td className="px-2 py-1 text-right">{data.categories.length}</td>
                          <td className="px-2 py-1 text-right">{revenue}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={handleBulkImportCancel}
                  className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded"
                >
                  &#x130;ptal
                </button>
                <button
                  onClick={handleBulkImportConfirm}
                  disabled={isBulkImporting}
                  className="px-3 py-1.5 text-sm text-white bg-green-600 hover:bg-green-700 rounded disabled:opacity-50"
                >
                  {isBulkImporting ? '\u0130\u015Fleniyor...' : 'T\u00FCm\u00FCn\u00FC Import Et'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Copy Country Defaults Section */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Copy className="w-5 h-5 text-amber-600" />
            <h4 className="font-medium text-amber-900">&#xDC;lke Verisi Kopyala</h4>
          </div>
          <button
            onClick={handleOpenCopyCountryModal}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-amber-700 bg-white hover:bg-amber-50 border border-amber-200 rounded-lg"
          >
            <Copy className="w-4 h-4" />
            Kopyala
          </button>
        </div>
        <p className="text-xs text-amber-700">
          Sat&#x131;&#x15F; verisi olmayan &#xFC;lkeler i&#xE7;in, mevcut bir &#xFC;lkenin gider oranlar&#x131;n&#x131; baz al&#x131;n.
          &#xD6;rne&#x11F;in UK verilerini G&#xFC;ney Afrika i&#xE7;in kullanabilirsiniz.
        </p>
      </div>

      {/* Copy Country Modal */}
      {showCopyCountryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            ref={copyCountryModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="copy-country-modal-title"
            className="bg-white rounded-xl shadow-xl max-w-lg w-full"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 id="copy-country-modal-title" className="font-semibold text-slate-800">&#xDC;lke Verisi Kopyala</h3>
              <button onClick={() => setShowCopyCountryModal(false)} className="p-1 hover:bg-slate-100 rounded" aria-label="Kapat">
                <X className="w-5 h-5 text-slate-500" aria-hidden="true" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Source Country */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Kaynak &#xDC;lke (verisi olan)
                </label>
                <select
                  value={copyCountrySource}
                  onChange={(e) => setCopyCountrySource(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  {countriesWithData.map(c => {
                    const country = selectedMarketplace.countries.find(x => x.code === c.countryCode);
                    return (
                      <option key={c.countryCode} value={c.countryCode}>
                        {country?.flag || '\u{1F3F3}\uFE0F'} {country?.name || c.countryCode} ({c.categoryCount} kategori, {c.fulfillmentTypes.join('/')})
                      </option>
                    );
                  })}
                </select>
                {countriesWithData.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">
                    Hen&#xFC;z hi&#xE7;bir &#xFC;lkede gider verisi yok. &#xD6;nce Profit Analyzer'dan import yap&#x131;n.
                  </p>
                )}
              </div>

              {/* Target Country */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Hedef &#xDC;lke (verisi olmayan)
                </label>
                <select
                  value={copyCountryTarget}
                  onChange={(e) => setCopyCountryTarget(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="">-- Se&#xE7;in --</option>
                  {selectedMarketplace.countries.filter(c => c.isActive).map(country => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Fulfillment Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Fulfillment Tipi
                </label>
                <div className="flex gap-4">
                  {(['both', 'FBA', 'FBM'] as const).map(ft => (
                    <label key={ft} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="copyFulfillment"
                        checked={copyFulfillmentType === ft}
                        onChange={() => setCopyFulfillmentType(ft)}
                        className="w-4 h-4 text-amber-600 border-slate-300"
                      />
                      <span className="text-sm text-slate-700">
                        {ft === 'both' ? 'T\u00FCm\u00FC (FBA + FBM)' : ft}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Overwrite Option */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={copyOverwrite}
                    onChange={(e) => setCopyOverwrite(e.target.checked)}
                    className="w-4 h-4 text-amber-600 border-slate-300 rounded"
                  />
                  <span className="text-sm text-slate-700">
                    Mevcut verilerin &#xFC;zerine yaz
                  </span>
                </label>
                <p className="text-xs text-slate-500 ml-6 mt-1">
                  &#x130;&#x15F;aretlenmezse, hedef &#xFC;lkede zaten var olan kategoriler atlan&#x131;r.
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowCopyCountryModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  &#x130;ptal
                </button>
                <button
                  onClick={handleCopyCountryConfirm}
                  disabled={isCopyingCountry || !copyCountrySource || !copyCountryTarget || countriesWithData.length === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50"
                >
                  {isCopyingCountry ? 'Kopyalan\u0131yor...' : 'Kopyala'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import/Export */}
      <div className="flex items-center gap-2 pt-2">
        <input
          type="file"
          accept=".csv"
          onChange={handleImportExpenseRates}
          className="hidden"
          id="expense-csv-import"
        />
        <label
          htmlFor="expense-csv-import"
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg cursor-pointer"
        >
          <Upload className="w-4 h-4" />
          CSV Y&#xFC;kle
        </label>
        <button
          onClick={handleExportExpenseRates}
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg"
        >
          <Download className="w-4 h-4" />
          CSV &#x130;ndir
        </button>
      </div>

      {/* Filter Row */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-lg">
        <select
          value={expenseFilterCountry}
          onChange={(e) => setExpenseFilterCountry(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white"
        >
          <option value="">T&#xFC;m &#xDC;lkeler</option>
          {selectedMarketplace.countries.filter(c => c.isActive).map(country => (
            <option key={country.code} value={country.code}>
              {country.flag} {country.name}
            </option>
          ))}
        </select>
        <select
          value={expenseFilterFulfillment}
          onChange={(e) => setExpenseFilterFulfillment(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white"
        >
          <option value="">T&#xFC;m Fulfillment</option>
          {selectedMarketplace.fulfillmentOptions?.map(f => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
        {expenseFilterCategory && (
          <button
            onClick={() => setExpenseFilterCategory('')}
            className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-600 bg-indigo-50 rounded"
          >
            <X className="w-3 h-3" />
            {expenseFilterCategory}
          </button>
        )}
        <button
          onClick={() => {
            const firstCountry = selectedMarketplace.countries.find(c => c.isActive);
            setNewExpense({
              countryCode: firstCountry?.code || '',
              category: categories[0] || '',
              fulfillmentType: selectedMarketplace.fulfillmentOptions?.[0] || 'FBA',
              targetMargin: 20,
              columnDefaults: Object.fromEntries(selectedMarketplace.columns.map(c => [c.key, c.defaultValue || 0])),
            });
            setIsAddingExpense(true);
          }}
          className="ml-auto flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
        >
          <Plus className="w-4 h-4" />
          Yeni Ekle
        </button>
      </div>

      {/* Expense Rates List */}
      {(() => {
        const allDefaults = categoryDefaults.filter(d => d.marketplaceId === selectedMarketplace.id);

        // Group by category if no filters
        if (!expenseFilterCountry && !expenseFilterCategory && !expenseFilterFulfillment) {
          const categoryGroups = allDefaults.reduce((acc, d) => {
            if (!acc[d.category]) acc[d.category] = [];
            acc[d.category].push(d);
            return acc;
          }, {} as Record<string, CategoryColumnDefaults[]>);

          return (
            <div className="space-y-2">
              <div className="text-sm text-slate-500 mb-3">
                Kategori se&#xE7;in veya &#xFC;lke filtreleyin
              </div>
              {Object.entries(categoryGroups)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([category, items]) => {
                  const uniqueCountries = Array.from(new Set(items.map(i => i.countryCode)));
                  const hasFBA = items.some(i => i.fulfillmentType.includes('FBA'));
                  const hasFBM = items.some(i => i.fulfillmentType.includes('FBM'));

                  return (
                    <div
                      key={category}
                      onClick={() => setExpenseFilterCategory(category)}
                      className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100 hover:border-indigo-300 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-slate-800">{category}</span>
                        <div className="flex gap-1">
                          {hasFBA && (
                            <span className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-700">FBA</span>
                          )}
                          {hasFBM && (
                            <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">FBM</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex -space-x-1">
                          {uniqueCountries.slice(0, 5).map(code => {
                            const c = selectedMarketplace.countries.find(x => x.code === code);
                            return (
                              <span key={code} className="text-sm" title={code}>
                                {c?.flag || '\u{1F3F3}\uFE0F'}
                              </span>
                            );
                          })}
                          {uniqueCountries.length > 5 && (
                            <span className="text-xs text-slate-500 ml-1">+{uniqueCountries.length - 5}</span>
                          )}
                        </div>
                        <span className="text-xs text-slate-500">
                          {uniqueCountries.length} &#xFC;lke
                        </span>
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  );
                })}
            </div>
          );
        }

        // Filter and show detailed list
        const filteredDefaults = allDefaults.filter(d =>
          (!expenseFilterCountry || d.countryCode === expenseFilterCountry) &&
          (!expenseFilterCategory || d.category === expenseFilterCategory) &&
          (!expenseFilterFulfillment || d.fulfillmentType === expenseFilterFulfillment)
        );

        if (filteredDefaults.length === 0) {
          return (
            <div className="text-center py-8 text-slate-500">
              <DollarSign className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="mb-2">Bu filtrelerle e&#x15F;le&#x15F;en gider oran&#x131; bulunamad&#x131;.</p>
            </div>
          );
        }

        return (
          <div className="space-y-2">
            {/* Show category header when category is selected */}
            {expenseFilterCategory && !expenseFilterCountry && (
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-800">{expenseFilterCategory}</span>
                  <span className="text-sm text-slate-500">
                    ({filteredDefaults.length} kay&#x131;t)
                  </span>
                </div>
                <button
                  onClick={() => setExpenseFilterCategory('')}
                  className="text-xs text-indigo-600 hover:text-indigo-800"
                >
                  &#x2190; T&#xFC;m Kategoriler
                </button>
              </div>
            )}
            {filteredDefaults
              .sort((a, b) => `${a.countryCode}-${a.category}-${a.fulfillmentType}`.localeCompare(`${b.countryCode}-${b.category}-${b.fulfillmentType}`))
              .map(expense => {
                const country = selectedMarketplace.countries.find(c => c.code === expense.countryCode);
                const isExpanded = expandedExpenseId === expense.id;

              return (
                <div key={expense.id} className="border border-slate-200 rounded-lg overflow-hidden">
                  {/* Header Row */}
                  <div
                    className="flex items-center justify-between p-3 bg-slate-50 cursor-pointer hover:bg-slate-100"
                    onClick={() => setExpandedExpenseId(isExpanded ? null : expense.id)}
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      <span className="text-lg">{country?.flag || '\u{1F3F3}\uFE0F'}</span>
                      <span className="font-medium text-slate-800">{expense.category}</span>
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        expense.fulfillmentType.includes('FBA') ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {expense.fulfillmentType}
                      </span>
                      {expense.dataSource === 'imported' && (
                        <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700">
                          Profit Analyzer
                        </span>
                      )}
                      <span className="text-sm text-slate-500">
                        Hedef: <span className="font-medium text-green-600">{Number(expense.targetMargin).toFixed(1)}%</span>
                      </span>
                      <span className="text-xs text-slate-400 hidden md:inline-flex gap-2">
                        | Sell: {Number(expense.columnDefaults?.sellingPercent || 0).toFixed(1)}%
                        | FBA: {Number(expense.columnDefaults?.fbaFeePercent || 0).toFixed(1)}%
                        | Ads: {Number(expense.columnDefaults?.adsPercent || 0).toFixed(1)}%
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Copy Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCopyFromExpense(expense);
                          setNewExpense({
                            countryCode: expense.countryCode,
                            category: expense.category,
                            fulfillmentType: expense.fulfillmentType === 'FBA' ? 'FBM' : 'FBA',
                            targetMargin: expense.targetMargin,
                            columnDefaults: { ...expense.columnDefaults },
                          });
                          setIsAddingExpense(true);
                        }}
                        className="p-1.5 text-slate-500 hover:bg-slate-200 rounded"
                        title="Kopyala"
                      >
                        <Copy className="w-4 h-4" />
                      </button>

                      {/* Delete Button */}
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (window.confirm('Bu gider oran\u0131n\u0131 silmek istedi\u011Finizden emin misiniz?')) {
                            await deleteCategoryColumnDefaults(expense.id);
                            setCategoryDefaults(prev => prev.filter(d => d.id !== expense.id));
                          }
                        }}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (() => {
                    // Filter columns based on fulfillment type
                    const isFBA = expense.fulfillmentType.includes('FBA');
                    const hiddenForFBA = ['fbmCostPercent', 'ddpPercent', 'customsPercent'];
                    const hiddenForFBM = ['fbaFeePercent', 'fbaCostPercent'];
                    const hiddenKeys = isFBA ? hiddenForFBA : hiddenForFBM;

                    const visibleCols = selectedMarketplace.columns.filter(col => !hiddenKeys.includes(col.key));

                    return (
                    <div className="p-4 border-t border-slate-200 bg-white">
                      <div className="grid grid-cols-6 gap-3">
                        {/* Target Margin */}
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Hedef Marj
                          </label>
                          <input
                            type="number"
                            value={Number(expense.targetMargin).toFixed(1)}
                            onChange={async (e) => {
                              const newMargin = parseFloat(e.target.value) || 0;
                              const updated = { ...expense, targetMargin: newMargin };
                              await upsertCategoryColumnDefaults(updated);
                              setCategoryDefaults(prev => prev.map(d => d.id === expense.id ? { ...d, targetMargin: newMargin } : d));
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                            step="0.1"
                          />
                        </div>

                        {/* Column Values - filtered by fulfillment type */}
                        {visibleCols.map(col => (
                          <div key={col.id}>
                            <label className="block text-xs font-medium text-slate-600 mb-1 truncate" title={col.name}>
                              {col.name.replace(' (%)', '').replace(' Percent', '').replace('Percent', '')}
                            </label>
                            <input
                              type="number"
                              value={Number(expense.columnDefaults[col.key] || 0).toFixed(1)}
                              onChange={async (e) => {
                                const newValue = parseFloat(e.target.value) || 0;
                                const newDefaults = { ...expense.columnDefaults, [col.key]: newValue };
                                const updated = { ...expense, columnDefaults: newDefaults };
                                await upsertCategoryColumnDefaults(updated);
                                setCategoryDefaults(prev => prev.map(d => d.id === expense.id ? { ...d, columnDefaults: newDefaults } : d));
                              }}
                              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                              step="0.1"
                            />
                          </div>
                        ))}
                      </div>

                      <div className="mt-3 text-xs text-slate-400 flex flex-wrap gap-4">
                        <span>Son g&#xFC;ncelleme: {expense.updatedAt.toLocaleString('tr-TR')}</span>
                        {expense.dataSource === 'imported' && (
                          <>
                            <span className="text-purple-600">Kaynak: Profit Analyzer</span>
                            {expense.sampleSize && <span className="text-purple-600">&#xD6;rnek: {expense.sampleSize} i&#x15F;lem</span>}
                            {expense.periodStart && expense.periodEnd && (
                              <span className="text-purple-600">D&#xF6;nem: {expense.periodStart.toLocaleDateString('tr-TR')} - {expense.periodEnd.toLocaleDateString('tr-TR')}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Add New Modal */}
      {isAddingExpense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            ref={addExpenseModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-expense-modal-title"
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
              <h3 id="add-expense-modal-title" className="font-semibold text-slate-800">
                {copyFromExpense ? 'Gider Oran\u0131 Kopyala' : 'Yeni Gider Oran\u0131'}
              </h3>
              <button onClick={() => { setIsAddingExpense(false); setCopyFromExpense(null); }} className="p-1 hover:bg-slate-100 rounded" aria-label="Kapat">
                <X className="w-5 h-5 text-slate-500" aria-hidden="true" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Selection Row */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">&#xDC;lke</label>
                  <select
                    value={newExpense.countryCode}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, countryCode: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  >
                    {selectedMarketplace.countries.filter(c => c.isActive).map(country => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Kategori</label>
                  <select
                    value={newExpense.category}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    {!categories.includes(newExpense.category) && newExpense.category && (
                      <option value={newExpense.category}>{newExpense.category}</option>
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fulfillment</label>
                  <select
                    value={newExpense.fulfillmentType}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, fulfillmentType: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  >
                    {selectedMarketplace.fulfillmentOptions?.map(f => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Target Margin */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Hedef Kar Marj&#x131; (%)
                </label>
                <input
                  type="number"
                  value={newExpense.targetMargin}
                  onChange={(e) => setNewExpense(prev => ({ ...prev, targetMargin: parseFloat(e.target.value) || 0 }))}
                  className="w-32 px-3 py-2 border border-slate-200 rounded-lg"
                  step="0.5"
                />
              </div>

              {/* Column Values */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Gider Oranlar&#x131;</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {selectedMarketplace.columns.map(col => (
                    <div key={col.id} className="flex items-center gap-2">
                      <label className="text-sm text-slate-600 flex-1">{col.name}</label>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          value={newExpense.columnDefaults[col.key] || 0}
                          onChange={(e) => setNewExpense(prev => ({
                            ...prev,
                            columnDefaults: { ...prev.columnDefaults, [col.key]: parseFloat(e.target.value) || 0 }
                          }))}
                          className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded text-right"
                          step="0.1"
                        />
                        <span className="text-xs text-slate-500 w-4">
                          {col.type === 'percent' ? '%' : ''}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
                <button
                  onClick={() => { setIsAddingExpense(false); setCopyFromExpense(null); }}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  &#x130;ptal
                </button>
                <button
                  onClick={async () => {
                    if (!newExpense.countryCode || !newExpense.category || !newExpense.fulfillmentType) return;

                    await upsertCategoryColumnDefaults({
                      marketplaceId: selectedMarketplace.id,
                      countryCode: newExpense.countryCode,
                      category: newExpense.category,
                      fulfillmentType: newExpense.fulfillmentType,
                      targetMargin: newExpense.targetMargin,
                      columnDefaults: newExpense.columnDefaults,
                      dataSource: 'manual',
                    });

                    const defaults = await getCategoryDefaultsByMarketplace(selectedMarketplace.id);
                    setCategoryDefaults(defaults);
                    setIsAddingExpense(false);
                    setCopyFromExpense(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesTab;
